/**
 * When the save indicator plays its full moment, a tick, or nothing.
 *
 * Pure: no timers, no stores, no DOM. The indicator feeds it what happened and
 * when, and it answers with what to show. Every rule in Olivia's handoff §2
 * lives here so it can be tested without a browser.
 *
 *   - The FULL moment plays for exactly four events: the first save of a new
 *     document, a named version, back online and synced, and recovery after a
 *     failure.
 *   - At most one full moment per 60 s. A second qualifying event inside that
 *     window plays as a tick.
 *   - A TICK plays once, on the first completed save after a typing burst
 *     ends (3 s without a keystroke). Saves during a burst change nothing.
 *
 * Design: lilia-docs/design-handoffs/2026-09-27-saved-moment/ §2.
 */

/** The four events that earn the full moment. */
export type FullEvent = "first-save" | "version" | "back-online" | "recovered";

export type MomentKind = "full" | "tick";

export const FULL_MOMENT_THROTTLE_MS = 60_000;
export const BURST_QUIET_MS = 3_000;

export interface MomentState {
  /** When the last full moment played; null until one has. */
  lastFullAt: number | null;
  /** The last keystroke, or null before any. */
  lastKeystrokeAt: number | null;
  /** A burst was typed and its tick has not played yet. */
  tickOwed: boolean;
  /** A save has completed since the last keystroke, with nothing left over. */
  savedSinceKeystroke: boolean;
}

export const INITIAL_MOMENT_STATE: MomentState = {
  lastFullAt: null,
  lastKeystrokeAt: null,
  tickOwed: false,
  savedSinceKeystroke: false,
};

export type MomentInput =
  /** The author changed the document (typing, a title edit, a paste). */
  | { type: "keystroke"; at: number }
  /** An ordinary save reached the server. `pending` is what is still unsent. */
  | { type: "save-completed"; at: number; pending: number }
  /** The indicator's timer, fired BURST_QUIET_MS after a keystroke. */
  | { type: "burst-check"; at: number }
  /** One of the four events that can earn the full moment. */
  | { type: "qualifying"; event: FullEvent; at: number };

export interface MomentDecision {
  moment: MomentKind | null;
  /** Set when the decision came from a qualifying event, full or throttled. */
  event?: FullEvent;
}

export interface MomentStep {
  state: MomentState;
  decision: MomentDecision;
}

const NOTHING: MomentDecision = { moment: null };

function burstIsOver(state: MomentState, at: number): boolean {
  return state.lastKeystrokeAt !== null && at - state.lastKeystrokeAt >= BURST_QUIET_MS;
}

export function reduceMoment(state: MomentState, input: MomentInput): MomentStep {
  switch (input.type) {
    case "keystroke":
      return {
        state: { ...state, lastKeystrokeAt: input.at, tickOwed: true, savedSinceKeystroke: false },
        decision: NOTHING,
      };

    case "save-completed": {
      const next: MomentState = { ...state, savedSinceKeystroke: input.pending === 0 };
      // During a burst nothing moves; the burst's end will settle the debt.
      if (state.tickOwed && burstIsOver(state, input.at)) {
        return { state: { ...next, tickOwed: false }, decision: { moment: "tick" } };
      }
      return { state: next, decision: NOTHING };
    }

    case "burst-check": {
      // A keystroke since the timer was set moved the burst's end on.
      if (!state.tickOwed || !burstIsOver(state, input.at)) return { state, decision: NOTHING };
      // Its last save already landed while the author was still typing: the
      // work is safe now, so this is the moment to say so. Otherwise the tick
      // waits for the save that is still on its way.
      if (state.savedSinceKeystroke) {
        return { state: { ...state, tickOwed: false }, decision: { moment: "tick" } };
      }
      return { state, decision: NOTHING };
    }

    case "qualifying": {
      const throttled =
        state.lastFullAt !== null && input.at - state.lastFullAt < FULL_MOMENT_THROTTLE_MS;
      // Either way this moment covers the save in flight, so the burst's tick
      // is paid: a full moment followed by a tick for the same save would be
      // the indicator repeating itself.
      const base: MomentState = { ...state, tickOwed: false };
      if (throttled) {
        return { state: base, decision: { moment: "tick", event: input.event } };
      }
      return {
        state: { ...base, lastFullAt: input.at },
        decision: { moment: "full", event: input.event },
      };
    }
  }
}

/** When the indicator should fire its next `burst-check`, or null for none. */
export function nextBurstCheckAt(state: MomentState): number | null {
  if (!state.tickOwed || state.lastKeystrokeAt === null) return null;
  return state.lastKeystrokeAt + BURST_QUIET_MS;
}

// ── Reading the sync store ──────────────────────────────────────────────────

/** The sync store's states (mirrors SyncState, kept local to stay pure). */
export type SyncPhase = "upToDate" | "syncing" | "offline" | "error";

export interface SyncSnapshot {
  state: SyncPhase;
  /** ms since epoch of the last successful save, or null for never. */
  lastSyncedAt: number | null;
  pendingCount: number;
}

export type SyncTransition =
  | { type: "qualifying"; event: Exclude<FullEvent, "version"> }
  | { type: "save-completed" }
  | null;

/**
 * What a change in the sync store means for the indicator.
 *
 * `stable` is the last state that was not `syncing`: a save out of `error`
 * passes through `syncing` when it is slow, and it is still a recovery.
 * `neverSaved` is true while the document has never had the author's work
 * reach the server — the indicator's `new` state. `authorTyped` says whether
 * the author has changed it since it opened: the editor can save a new
 * document on its own as it settles (normalising its first empty paragraph),
 * and that is not the first save anyone should be told about.
 */
export function classifySyncTransition(
  stable: SyncPhase,
  next: SyncSnapshot,
  prevLastSyncedAt: number | null,
  neverSaved: boolean,
  authorTyped = true,
): SyncTransition {
  if (next.state !== "upToDate") return null;
  const saved = next.lastSyncedAt !== null && next.lastSyncedAt !== prevLastSyncedAt;

  if (stable === "offline") return { type: "qualifying", event: "back-online" };
  if (stable === "error") return { type: "qualifying", event: "recovered" };
  if (!saved) return null;
  if (neverSaved) return authorTyped ? { type: "qualifying", event: "first-save" } : null;
  return { type: "save-completed" };
}
